/* eslint-disable object-curly-newline */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components';
import { handleValidation, extractValidation } from '../../utils/form';
import { store } from '../../store';

// actions
import {
  getEnrollment,
  createEnrollment,
  getEnrollmentConfig,
  getEnrollmentOptions,
} from '../../actions/enrollment';
import { getPlan, getAdditionalPlanInfo } from '../../actions/plan';
import { getAgent, getShopper } from '../../actions/user';
import { getQuote } from '../../actions/quote';
import { setAuthFromCookie, checkIfLoggedIn } from '../../actions/session';
import { validateTheme, loadTheme } from '../../actions/theme';
import { loadScript } from '../../actions/script';

// utils
import { telephonyEnabled, injectTelephonyRouting } from '../../utils/telephony';
import { getParam } from '../../common/utils/qs';
import { fmtAgent, fmtShopper } from '../../common/utils/format';
import { removeScriptOnlyRouting } from '../../utils/script';
import { setCookie, getCookie } from '../../common/utils/cookie';
import { evaluateConditions } from '../../common/utils/enrollment';

function EnrollmentLoader() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errMsg, setErrMsg] = useState(false);
  const [disabledMsg, setDisabledMsg] = useState(false);
  const [loading, setLoading] = useState(false);
  const quoteID = getParam('quoteID');

  async function loadEnrollment() {
    setLoading(true);

    // NOTE(santeyio): not the nicest thing to instantiate all these variables
    // at the top like this, but I wanted to have fairly granular error handling.
    // Declaring variables inside of a try/catch block scopes the variables
    // so they then can't be accessed outside the try/catch. I think the
    // async/await syntax here makes it cleaner than promise chaining (even
    // with all the variables we have to declare up here).
    /* eslint-disable one-var-declaration-per-line,one-var */
    let enrollmentExists, carrier, planYear, planID, agentUsername,
      enrollment, quote, validation, formConfig, shopperUsername,
      shopper, agent, theme, planMerge, filteredForm, meetType;

    // first get auth from cookie
    try {
      setAuthFromCookie();
      await checkIfLoggedIn();
    } catch (err) {
      setLoading(false);
      setErrMsg(<span>Invalid authentication credentials</span>);
      return;
    }

    // make sure theme is up to date by fetching latest
    try {
      const hash = await validateTheme();
      theme = await loadTheme(hash);
    } catch (err) {
      setErrMsg(<span>There was a problem loading the theme</span>);
      setLoading(false);
      console.log('There was a problem loading the theme.');
    }

    // fetch quote info
    try {
      dispatch({ type: 'CLEAR' });
      quote = await getQuote(quoteID);
      ({
        carrier,
        planYear,
        planID,
        shopperUsername,
        createdByUsername: agentUsername,
      } = quote);
      enrollmentExists = Boolean(quote.enrollment);

      // when an enrollment is opened we need to set this cookie so that if a softphone
      // instance is running it knows the quote id to associate with the call record.
      // see src/components/softphone/index.js for code that watches for this cookie.
      setCookie('softphoneQuoteId', quoteID);
    } catch (err) {
      console.log(err);
      setErrMsg(<span>Unable to load the quote <i>{quoteID}</i></span>);
      setLoading(false);
      return;

    // fetch form configuration
    } try {
      formConfig = await getEnrollmentConfig(carrier, planYear);
      if (telephonyEnabled(theme)) {
        formConfig = injectTelephonyRouting(formConfig);
      }
      if (!theme.agentScript) {
        formConfig = removeScriptOnlyRouting(formConfig);
      }
      dispatch({ type: 'LOAD_ENROLLMENT_FORM', form: formConfig });
      // if form is configured to be disabled, stop loading and display message
      if (formConfig.disabled) {
        if (formConfig.disabledMsg) {
          setDisabledMsg(<span>{formConfig.disabledMsg}</span>);
        } else {
          setDisabledMsg(<span>Enrollment for this plan is not currently enabled</span>);
        }
        setLoading(false);
        return;
      }
    } catch {
      setErrMsg(<span>Site Config {`enrollment_form_agent_${carrier}_${planYear}_json`} missing</span>);
      setLoading(false);
      return;

    // fetch shopper and agent data
    } try {
      // NOTE(santeyio): fmtAgent/Shopper is not called in the action itself
      // because we need the api formatting to load into the enrollment values
      shopper = await getShopper(shopperUsername);
      agent = await getAgent(agentUsername);
      dispatch({ type: 'SET_AGENT', agent: fmtAgent(agent) });
      dispatch({ type: 'SET_SHOPPER', shopper: fmtShopper(shopper) });
    } catch (err) {
      console.log(err);
      setErrMsg(<span>There was an error loading the user data</span>);
      setLoading(false);
      return;

    // fetch enrollment data
    } try {
      if (!enrollmentExists) {
        enrollment = await createEnrollment(quoteID, fmtShopper(shopper));
        dispatch({ type: 'SET_ALERT', payload: { resumeEnrollmentEmail: true } });
      }

      // TODO(santeyio): make this call conditional by updating the api to set last_accessed on creation
      enrollment = await getEnrollment(quoteID);

      dispatch({ type: 'SET_ALERT', payload: { formSpecificAlert: true } });
      dispatch({
        type: 'SET_ENROLLMENT_FIELD',
        payload: enrollment,
      });
    } catch (err) {
      console.log(err);
      setErrMsg(<span>Unable to load or create enrollment for <i>{quoteID}</i></span>);
      setLoading(false);
      return;

    // fetch additional form options and data
    } try {
      const [
        sepOptions,
        raceOptions,
        ethnicityOptions,
        fieldDefaults,
        plan,
        // additionalPlanInfo,
      ] = await Promise.all([
        getEnrollmentOptions('sep_options'),
        getEnrollmentOptions('race_options'),
        getEnrollmentOptions('ethnicity_options'),
        getEnrollmentOptions('field_defaults'),
        getPlan(planID),
        // getAdditionalPlanInfo(planID, quote.county),
      ]);
      const additionalPlanInfo = await getAdditionalPlanInfo(planID, quote.county) || {};

      // extract individual field defaults from config
      const defaultValidation = {};
      const defaultLabels = {};
      Object.entries(fieldDefaults).forEach(([ key, value ]) => {
        defaultValidation[key] = value.validation;
        defaultLabels[key] = value.label;
      });

      // extract validation from form config and override default validation with it
      const customValidation = extractValidation(formConfig);

      validation = { ...defaultValidation, ...customValidation };

      // merge additional plan info (for anthem) into plan
      planMerge = {
        ...plan,
        plan: {
          ...additionalPlanInfo,
          ...plan.plan,
        },
      };

      dispatch({ type: 'LOAD_ENROLLMENT', payload: enrollment });
      dispatch({
        type: 'SET_PLAN',
        plan: planMerge,
      });
      dispatch({ type: 'SET_QUOTE', quote });
      dispatch({ type: 'LOAD_VALIDATION', validation });
      dispatch({ type: 'LOAD_LABELS', labels: defaultLabels });
      dispatch({
        type: 'LOAD_FORM_OPTIONS',
        sep: sepOptions.value,
        race: raceOptions.value,
        ethnicity: ethnicityOptions.value,
      });

      // some fields require checking against conditions determined by plan data
      // for validation. Here we inject those values into store.values so that
      // the validation can read them.
      const isCSNP = (plan.plan.SNPIND === 1); // if SNPIND === 1 then it's CSNP
      const isDSNP = (plan.plan.SNPIND === 2); // if SNPIND === 2 then it's DSNP
      const isISNP = (plan.plan.SNPIND === 3); // if SNPIND === 3 then it's ISNP
      console.log('theme.alwaysUseMeetType: ', theme.alwaysUseMeetType);
      const { alwaysUseMeetType, hideConsumerPortalSignatureOptions } = theme;
      if (alwaysUseMeetType) {
        setCookie('meetType', alwaysUseMeetType);
        meetType = alwaysUseMeetType;
      } else {
        meetType = (getCookie('meetType') || 'inPerson'); // need this in redux for conditional displays
      }
      dispatch({
        type: 'SET_FIELD_VALUES',
        values: {
          isDSNP,
          isCSNP,
          isISNP,
          meetType,
          hideConsumerPortalSignatureOptions,
        },
      });
      // save all plan fields for good measure
      dispatch({ type: 'SET_FIELD_VALUES', values: { plan: planMerge.plan } });
      // save all quote fields for good measure
      dispatch({
        type: 'SET_FIELD_VALUES',
        // remove enrollment data from quote before saving it in enrollment fields
        values: { quote: { ...quote, enrollment: undefined } },
      });
    } catch (err) {
      console.log(err);
      setErrMsg('There was an error loading the configuration for this form.');
      setLoading(false);
      return;

    // load script data into redux
    } try {
      const script = await loadScript(planYear);
      dispatch({ type: 'LOAD_SCRIPT', script });
    } catch (err) {
      console.log(err);
      setErrMsg(`No script found for plan year ${planYear}`);
      setLoading(false);
      return;

    // load fetched form data into redux
    } try {
      const { fields } = enrollment;

      // pull out shopper.data.beq keys from shopper
      const { data: { beq: beqData = {} } = {} } = shopper;
      // in shopper.data.beq keys need to be snake cased
      const beqReformatted = {
        street1: beqData.address,
        part_a_effective_date: beqData.partAEffectiveDate,
        part_b_effective_date: beqData.partBEffectiveDate,
      };

      // remove some of the longer data items from the shopper
      // before storing the shopper data in the enrollment fields
      const filteredShopper = {
        ...shopper,
        ...beqReformatted,
        quotes: undefined,
        campaigns: undefined,
        agency: undefined,
        data: undefined,
        email: shopper.contact_email || shopper.email || undefined,
      };
      const mergedFields = {
        ...filteredShopper,
        ...fields,
        meetType, // override the previously saved meetType as it may be different
      };

      // iterate all fields and set values and validation status for each field
      // skip over plan and quote so we have the most up to date info on each
      Object.keys(mergedFields).forEach(name => {
        const skipFields = ['plan', 'quote'];
        if (!skipFields.includes(name)) {
          const value = mergedFields[name];
          const validationRegex = validation[name];
          // Populate the form with existing shopper data.
          // We want to set and validate 'bool false' values and 'int 0' values,
          // but not undefined / null / blank string values.
          if (value !== null && value !== undefined && value !== '') {
            handleValidation({ validationRegex, value, name });
            dispatch({ type: 'SET_FIELD_VALUE', name, value });
          }
        }
      });
    } catch (err) {
      console.log(err);
      setErrMsg('There was a problem setting up the enrollment form.');
      setLoading(false);

    // remove any sections or pages that have displayConditions that don't evaluate true
    } try {
      const { values } = store.getState();
      filteredForm = formConfig.form.map(section => {
        let displaySection = true;

        if (section.displayConditions) {
          displaySection = evaluateConditions({
            conditions: section.displayConditions,
            currentValues: values,
          });
        }

        if (displaySection) {
          const filteredPages = (section.pages || []).map(page => {
            let displayPage = true;

            // edge case for recording acknowlegement page; it is dependent
            // on a cookie value not a redux value
            if (page.route === '/recording') {
              if (meetType === 'inPerson') return undefined;
            }

            if (page.displayConditions) {
              displayPage = evaluateConditions({
                conditions: page.displayConditions,
                currentValues: values,
              });
            }

            if (displayPage) return page;

            return undefined;
          }).filter(page => (page !== undefined));

          return { ...section, pages: filteredPages };
        }

        return undefined;
      }).filter(section => (section !== undefined));

      // set filtered form in redux
      dispatch({ type: 'LOAD_ENROLLMENT_FORM', form: { ...formConfig, form: filteredForm } });
    } catch (err) {
      console.log(err);
      setErrMsg('There was a problem setting up the enrollment form.');
      setLoading(false);

    // navigate to either first route or summary if submitted
    } try {

      const rfmCarrier = carrier.trim().replaceAll(' ', '-');
      const enrollmentURLBase = `/enroll/${rfmCarrier}/${planYear}`;

      // if enrollment is submitted then go to summary page
      if (enrollment.submittedFlag) {
        navigate(`${enrollmentURLBase}/summary/summary`);
        return;
      }

      // else it isn't submitted so find first route and navigate there
      const {
        route: sectionURL,
        pages: [{ route: pageURL }],
      } = filteredForm[0];
      const enrollmentURL = `${enrollmentURLBase}${sectionURL}${pageURL}`; // eslint-disable-line
      setLoading(false);
      navigate(enrollmentURL);
    } catch (err) {
      console.log(err);
      setErrMsg('There was a problem setting up the enrollment form.');
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!loading) { // make sure that loadEnrollment is only called once
      loadEnrollment();
    }
  }, []);

  return (
    <div className="container">
      <div className="row mt-5">
        {loading ? (
          <>
            <div className="d-flex col-md-12 justify-content-center mt-5">
              <h3 className="text-muted">One moment while we load your enrollment...</h3>
            </div>
            <div className="d-flex col-md-12 justify-content-center mt-5">
              <Spinner pixelSize="75" />
            </div>
          </>
        ) : (
          (errMsg && (
            <>
              <div className="d-flex col-md-12 justify-content-center mt-5">
                <h3>Something went wrong loading your enrollment.</h3>
              </div>
              <div className="d-flex col-md-12 justify-content-center">
                <p className="text-muted">Please close this window and try again</p>
              </div>
              <div className="d-flex col-md-12 justify-content-center mt-5">
                <div className="alert alert-warning">
                  {errMsg}
                </div>
              </div>
            </>
          )) || (disabledMsg && (
            <div className="d-flex col-md-12 justify-content-center mt-5">
              <h3>{disabledMsg}</h3>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default EnrollmentLoader;
